import { useMemo } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useT, useLocalePath, useLang } from "@/i18n";
import { useSeo, absoluteUrl } from "@/lib/seo";
import { ArrowRight, CalendarCheck, CheckCircle2, Microscope, ShieldCheck, Stethoscope } from "lucide-react";

const DE = {
  inLanguage: "de-AT",
  metaTitle: "Darmsanierung Wien: Diagnostik & Begleitung | PRAXIS IB",
  metaDescription:
    "Darmsanierung in Wien – befundbasiert statt Kur von der Stange: Mikrobiom-Diagnostik, qPCR/NGS, individueller Aufbau und persönliche Begleitung in der Praxis (Zoom nach Vereinbarung).",
  keywords:
    "Darmsanierung Wien, Darmaufbau Wien, Mikrobiom Analyse Wien, Darmflora aufbauen, Reizdarm Wien, SIBO Wien, Darmgesundheit Wien, Stuhlanalyse Wien, komplementärmedizinische Therapeutin Wien",
  home: "Startseite",
  breadcrumb: "Darmsanierung Wien",
  eyebrow: "Praxis in Wien (Zoom n. Vereinbarung)",
  h1: "Darmsanierung in Wien: befundbasiert statt Kur von der Stange",
  intro:
    "Eine Darmsanierung ist kein Standardprogramm, das für alle gleich aussieht. Bevor etwas aufgebaut wird, muss klar sein, was tatsächlich vorliegt: welche Bakteriengruppen fehlen, welche überwiegen, wie es um Schleimhaut, Verdauungsleistung und Immunlage steht. Genau dort setze ich an – mit Diagnostik, Einordnung und einer Begleitung, die zu Ihrem Alltag passt.",
  quick: [
    "Erst Befund, dann Therapie – keine pauschalen Kuren",
    "Mikrobiologische und DNA-basierte Diagnostik (qPCR / NGS)",
    "Individueller Aufbau statt Standardprotokoll",
    "Praxis in Wien (Zoom nach Vereinbarung)",
  ],
  sections: [
    {
      h: "Was eine Darmsanierung wirklich bedeutet",
      p: [
        "Der Begriff „Darmsanierung“ suggeriert, der Darm ließe sich reinigen wie ein Rohr. Physiologisch trifft das nicht zu. Was tatsächlich möglich ist: die Zusammensetzung der Darmflora gezielt beeinflussen, die Schleimhaut und ihre Schutzschicht stabilisieren, die Verdauungsleistung verbessern und dem Immunsystem im Darm wieder eine geordnete Arbeitsgrundlage geben.",
        "Der Darm beherbergt den größten Teil des Immunsystems. Verschiebt sich die mikrobielle Balance, verändert sich die Produktion kurzkettiger Fettsäuren, die Schleimschicht wird dünner und immunologische Regelkreise geraten aus dem Takt. Beschwerden entstehen dann oft nicht nur im Verdauungstrakt, sondern auch auf der Haut, im Energiehaushalt oder in der Infektabwehr.",
      ],
    },
    {
      h: "Wann eine Darmsanierung sinnvoll ist",
      p: ["Häufige Anlässe, mit denen Klientinnen und Klienten zu mir in die Praxis kommen:"],
      list: [
        "Blähungen, Völlegefühl, wechselnder Stuhlgang, Reizdarm-Symptomatik",
        "Beschwerden nach wiederholten oder langen Antibiotikagaben",
        "Verdacht auf bakterielle Fehlbesiedelung des Dünndarms (SIBO)",
        "Unverträglichkeiten gegenüber Histamin, Fructose oder Lactose",
        "wiederkehrende Blasen- oder Scheideninfekte",
        "Hautbilder wie Neurodermitis, Akne oder Rosazea",
        "erhöhte Infektanfälligkeit und anhaltend niedrige Belastbarkeit",
      ],
    },
    {
      h: "Diagnostik: worauf ich die Therapie aufbaue",
      p: [
        "Am Anfang steht die Befundlage, nicht das Präparat. Je nach Fragestellung arbeite ich mit klassischer mikrobiologischer Stuhldiagnostik, mit Entzündungs- und Schleimhautparametern und – wo es die Fragestellung erfordert – mit DNA-basierter Diagnostik per qPCR und Next-Generation-Sequencing. Diese Verfahren erfassen auch Keime, die in der Kultur nicht anwachsen, und machen quantitative Verhältnisse sichtbar.",
        "Vorhandene ärztliche Befunde beziehe ich ausdrücklich mit ein. Viele Klientinnen und Klienten kommen mit umfangreichen Unterlagen, die bisher nie im Zusammenhang gelesen wurden. Häufig entsteht das entscheidende Bild erst, wenn Laborwerte, Vorgeschichte und Symptomatik nebeneinandergelegt werden.",
      ],
    },
    {
      h: "Wie der Aufbau in der Praxis abläuft",
      p: [
        "Nach der Befundbesprechung entsteht ein Plan, der in Phasen gedacht ist: entlasten, ordnen, aufbauen, stabilisieren. Welche Schwerpunkte dabei gesetzt werden, hängt vollständig von Ihrem Befund ab – von der Ernährungsstruktur über gezielte Mikronährstoffe bis zu ausgewählten mikrobiologischen Präparaten.",
        "Ebenso wichtig ist die Verlaufskontrolle. Wir prüfen in vereinbarten Abständen, was sich verändert hat, und passen an. Eine Darmsanierung ist ein Prozess über Wochen bis Monate, kein einmaliges Produkt.",
      ],
    },
    {
      h: "Zusammenarbeit mit Ärztinnen und Ärzten",
      p: [
        "Ich arbeite komplementärmedizinisch und ersetze keine ärztliche Diagnose oder Behandlung. Ein Teil meiner Klientinnen und Klienten wird mir von Ärztinnen und Ärzten überwiesen; in anderen Fällen empfehle ich eine ärztliche Abklärung, bevor wir weiterarbeiten. Diese klare Rollenverteilung ist die Grundlage für seriöse Begleitung.",
      ],
    },
  ],
  faqHeading: "Häufige Fragen zur Darmsanierung",
  faq: [
    {
      q: "Wie lange dauert eine Darmsanierung?",
      a: "Das hängt vom Befund und von der Vorgeschichte ab. Realistisch sind mehrere Wochen bis einige Monate, begleitet von Verlaufskontrollen. Kurzprogramme über wenige Tage verändern die Darmflora nicht nachhaltig.",
    },
    {
      q: "Brauche ich vorher eine Stuhlanalyse?",
      a: "In den meisten Fällen ja. Eine Diagnostik zeigt, welche Bereiche tatsächlich Unterstützung brauchen – und verhindert, dass Präparate ohne Grundlage eingesetzt werden.",
    },
    {
      q: "Was kostet die Begleitung?",
      a: "Ein Termin kostet 139 Euro inklusive Umsatzsteuer und dauert rund 60 Minuten – in der Praxis in Wien (Zoom nach Vereinbarung). Laborkosten werden direkt mit dem jeweiligen Labor verrechnet.",
    },
    {
      q: "Ist eine Darmsanierung auch online möglich?",
      a: "Nach persönlicher Absprache ja. Grundsätzlich findet das Erstgespräch vorzugsweise persönlich in der Praxis statt; Zoom-Termine sind für die weitere Begleitung nach Vereinbarung möglich.",
    },
    {
      q: "Wo in Wien findet die Begleitung statt?",
      a: "In meiner Praxis in Wien, Termine sind Montag, Dienstag und Freitag möglich. Zoom-Termine sind nach persönlicher Absprache möglich.",
    },
  ],
  linksHeading: "Passende Themenseiten",
  links: [
    { to: "/beschwerden/reizdarm-blaehbauch", label: "Reizdarmsyndrom" },
    { to: "/beschwerden/sibo-duenndarmfehlbesiedlung", label: "SIBO – Dünndarmfehlbesiedelung" },
    { to: "/beschwerden/nahrungsmittelunvertraeglichkeiten", label: "Nahrungsmittelunverträglichkeiten" },
    { to: "/blog", label: "Fachartikel im Magazin" },
  ],
  ctaHeading: "Sie möchten Ihren Darm fundiert abklären lassen?",
  ctaText:
    "Wir sehen uns Ihre Befunde in Ruhe gemeinsam an und legen fest, welche Diagnostik in Ihrem Fall sinnvoll ist – in der Praxis in Wien (Zoom nach Vereinbarung).",
  book: "Termin buchen",
  contact: "Kontakt aufnehmen",
  note: "Hinweis: Meine Arbeit ist komplementärmedizinisch und ergänzt die ärztliche Behandlung. Sie ersetzt weder eine medizinische Diagnose noch eine notwendige Therapie.",
};

const EN = {
  inLanguage: "en",
  metaTitle: "Gut restoration in Vienna: diagnostics & support | PRAXIS IB",
  metaDescription:
    "Gut restoration in Vienna based on real findings instead of generic cures: microbiome diagnostics, qPCR/NGS testing, individual rebuilding and personal support in practice (Zoom by arrangement).",
  keywords:
    "gut restoration Vienna, gut health Vienna, microbiome analysis Vienna, IBS Vienna, SIBO Vienna, stool analysis Vienna, complementary therapist Vienna",
  home: "Home",
  breadcrumb: "Gut restoration Vienna",
  eyebrow: "Practice in Vienna (Zoom by arrangement)",
  h1: "Gut restoration in Vienna: based on findings, not on generic cures",
  intro:
    "Gut restoration is not a standard programme that looks the same for everyone. Before anything is rebuilt, it has to be clear what is actually going on: which bacterial groups are missing, which dominate, and how the mucosa, digestive capacity and immune situation present. That is exactly where I start – with diagnostics, interpretation and support that fits your everyday life.",
  quick: [
    "Findings first, therapy second – no generic cures",
    "Microbiological and DNA-based diagnostics (qPCR / NGS)",
    "Individual rebuilding instead of a fixed protocol",
    "Practice in Vienna (Zoom by arrangement)",
  ],
  sections: [
    {
      h: "What gut restoration really means",
      p: [
        "The term suggests the gut could be cleaned like a pipe. Physiologically that is not the case. What is possible: influencing the composition of the gut flora, stabilising the mucosa and its protective layer, improving digestive capacity and giving the gut immune system an orderly basis to work from again.",
        "The gut hosts the largest part of the immune system. When the microbial balance shifts, the production of short-chain fatty acids changes, the mucus layer thins and immunological feedback loops fall out of step. Symptoms then often appear beyond digestion – on the skin, in energy levels or in susceptibility to infection.",
      ],
    },
    {
      h: "When gut restoration makes sense",
      p: ["Common reasons clients come to my practice:"],
      list: [
        "bloating, fullness, changing stool patterns, IBS-type symptoms",
        "complaints after repeated or prolonged courses of antibiotics",
        "suspected small intestinal bacterial overgrowth (SIBO)",
        "intolerance to histamine, fructose or lactose",
        "recurrent bladder or vaginal infections",
        "skin conditions such as atopic dermatitis, acne or rosacea",
        "increased susceptibility to infection and persistently low resilience",
      ],
    },
    {
      h: "Diagnostics: what I build the therapy on",
      p: [
        "Findings come first, not products. Depending on the question I work with classic microbiological stool diagnostics, inflammation and mucosal markers and – where required – DNA-based diagnostics via qPCR and next-generation sequencing. These methods also detect organisms that do not grow in culture and make quantitative relationships visible.",
        "Existing medical findings are explicitly included. Many clients arrive with extensive documentation that has never been read in context. Often the decisive picture only emerges once laboratory values, history and symptoms are placed side by side.",
      ],
    },
    {
      h: "How the rebuilding process works",
      p: [
        "After discussing the findings we develop a plan in phases: relieve, organise, rebuild, stabilise. Which priorities are set depends entirely on your findings – from nutritional structure and targeted micronutrients to selected microbiological preparations.",
        "Follow-up matters just as much. At agreed intervals we review what has changed and adjust. Gut restoration is a process over weeks to months, not a single product.",
      ],
    },
    {
      h: "Working together with doctors",
      p: [
        "I work in complementary medicine and do not replace medical diagnosis or treatment. Some of my clients are referred to me by doctors; in other cases I recommend medical assessment before we continue. This clear division of roles is the basis for serious support.",
      ],
    },
  ],
  faqHeading: "Frequently asked questions",
  faq: [
    {
      q: "How long does gut restoration take?",
      a: "That depends on the findings and your history. Realistically several weeks to a few months, accompanied by follow-ups. Short programmes lasting a few days do not change the gut flora sustainably.",
    },
    {
      q: "Do I need a stool analysis first?",
      a: "In most cases yes. Diagnostics show which areas actually need support and prevent products being used without a basis.",
    },
    {
      q: "What does the support cost?",
      a: "An appointment costs 139 euros including VAT and lasts around 60 minutes – at the practice in Vienna (Zoom by arrangement). Laboratory costs are billed directly by the respective laboratory.",
    },
    {
      q: "Is this possible online?",
      a: "By personal arrangement, yes. Ideally, the initial consultation takes place in person; Zoom appointments for follow-up support are possible by arrangement.",
    },
    {
      q: "Where in Vienna does this take place?",
      a: "At my practice in Vienna; appointments are available on Mondays, Tuesdays and Fridays. Zoom appointments are possible by personal arrangement.",
    },
  ],
  linksHeading: "Related topic pages",
  links: [
    { to: "/beschwerden/reizdarm-blaehbauch", label: "Irritable bowel syndrome" },
    { to: "/beschwerden/sibo-duenndarmfehlbesiedlung", label: "SIBO – small intestinal overgrowth" },
    { to: "/beschwerden/nahrungsmittelunvertraeglichkeiten", label: "Food intolerances" },
    { to: "/blog", label: "Articles in the magazine" },
  ],
  ctaHeading: "Would you like your gut assessed properly?",
  ctaText:
    "We take a calm look at your findings together and define which diagnostics make sense in your case – at the practice in Vienna (Zoom by arrangement).",
  book: "Book an appointment",
  contact: "Get in touch",
  note: "Please note: my work is complementary and supplements medical care. It replaces neither a medical diagnosis nor necessary treatment.",
};

const DarmsanierungWien = () => {
  const lang = useLang();
  const lp = useLocalePath();
  const t = useT(DE, EN);

  const jsonLd = useMemo(() => {
    const url = absoluteUrl(lang === "en" ? "/en/darmsanierung-wien" : "/darmsanierung-wien");
    return [
      {
        "@context": "https://schema.org",
        "@type": "MedicalWebPage",
        name: t.h1,
        description: t.metaDescription,
        url,
        inLanguage: t.inLanguage,
        about: {
          "@type": "MedicalTherapy",
          name: lang === "en" ? "Gut restoration" : "Darmsanierung",
          alternateName: lang === "en" ? ["Gut rebuilding"] : ["Darmaufbau", "Mikrobiomtherapie"],
        },
        author: { "@id": absoluteUrl("/#therapeutin") },
        publisher: { "@id": absoluteUrl("/#business") },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: t.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: t.home, item: absoluteUrl("/") },
          { "@type": "ListItem", position: 2, name: t.breadcrumb, item: url },
        ],
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useSeo({
    title: t.metaTitle,
    description: t.metaDescription,
    keywords: t.keywords,
    canonical: "/darmsanierung-wien",
    jsonLd,
    jsonLdId: "darmsanierung-jsonld",
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="py-12 md:py-16 bg-secondary/30">
          <div className="container mx-auto px-4 max-w-3xl">
            <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground mb-6">
              <Link to={lp("/")} className="hover:text-primary">
                {t.home}
              </Link>
              <span className="mx-2">/</span>
              <span className="text-foreground">{t.breadcrumb}</span>
            </nav>
            <span className="text-primary font-medium text-sm uppercase tracking-wider">{t.eyebrow}</span>
            <h1 className="text-3xl md:text-4xl font-light text-foreground mt-2 mb-4 break-words">{t.h1}</h1>
            <p className="text-muted-foreground leading-relaxed">{t.intro}</p>

            <ul className="mt-6 grid gap-2 sm:grid-cols-2">
              {t.quick.map((q) => (
                <li key={q} className="flex gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <span>{q}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <article className="py-12 md:py-16">
          <div className="container mx-auto px-4 max-w-3xl space-y-10">
            {t.sections.map((s) => (
              <section key={s.h}>
                <h2 className="text-xl md:text-2xl font-medium text-foreground mb-3 break-words">{s.h}</h2>
                {s.p.map((p) => (
                  <p key={p} className="text-muted-foreground leading-relaxed mb-3">
                    {p}
                  </p>
                ))}
                {"list" in s && s.list && (
                  <ul className="space-y-2 mt-2">
                    {s.list.map((item) => (
                      <li key={item} className="flex gap-2 text-muted-foreground">
                        <Microscope className="w-4 h-4 text-primary shrink-0 mt-1" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}

            <section>
              <h2 className="text-xl md:text-2xl font-medium text-foreground mb-4">{t.faqHeading}</h2>
              <div className="space-y-4">
                {t.faq.map((f) => (
                  <Card key={f.q} className="border-border">
                    <CardContent className="p-5">
                      <h3 className="font-medium text-foreground mb-2">{f.q}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <section className="bg-secondary/40 rounded-2xl p-6">
              <h2 className="text-xl font-medium text-foreground mb-4">{t.linksHeading}</h2>
              <ul className="space-y-2">
                {t.links.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={lp(l.to)}
                      className="inline-flex items-center gap-2 text-primary font-medium hover:gap-3 transition-all"
                    >
                      <ArrowRight className="w-4 h-4" />
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>

            <section className="border border-border rounded-2xl p-6">
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground leading-relaxed">{t.note}</p>
              </div>
            </section>

            <section className="text-center">
              <h2 className="text-2xl font-light text-foreground mb-3">{t.ctaHeading}</h2>
              <p className="text-muted-foreground mb-6">{t.ctaText}</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Button asChild>
                  <Link to={lp("/termin")}>
                    <CalendarCheck className="w-4 h-4 mr-2" />
                    {t.book}
                  </Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to={lp("/#kontakt")}>
                    <Stethoscope className="w-4 h-4 mr-2" />
                    {t.contact}
                  </Link>
                </Button>
              </div>
            </section>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default DarmsanierungWien;
